/**
 * Interactive hub — the default `spacecode` experience.
 *
 * When no target is provided, shows active sessions + recent repos.
 * When a bare name is provided, searches sessions + repos and lets
 * the user choose (auto-selects if exactly one match).
 */

import chalk from 'chalk';
import * as readline from 'readline';
import { loadSessions, activeSessions } from './sessions.js';
import type { SessionRecord } from './sessions.js';
import { runOutput } from './utils/exec.js';
import { logWarning, addBreadcrumb } from './posthog.js';
import { DEFAULT_API_URL, SANDBOX_WORKER_URL } from '@spacecode/shared/remote';
import { resolveAuthToken } from './config.js';

// ── Helpers ────────────────────────────────────────────────────────────

/** Safe shell identifier — only allows chars that cannot break out of a shell argument. */
const SAFE_SHELL_ID = /^[a-zA-Z0-9._-]+$/;

/**
 * Fetch the user's most recently updated GitHub repos via `gh`.
 * Returns `{ repos, failed }` so callers can distinguish "no repos" from "fetch error".
 */
async function fetchRecentRepos(limit = 6): Promise<{ repos: string[]; failed: boolean }> {
  const safeLimit = Math.max(1, Math.floor(limit));
  try {
    const json = await runOutput(
      `gh repo list --json nameWithOwner,updatedAt --limit ${safeLimit} --sort updated`,
      5_000,
    );
    const repos = JSON.parse(json);
    if (!Array.isArray(repos)) {
      logWarning('fetchRecentRepos: gh returned non-array', {});
      return { repos: [], failed: true };
    }
    return { repos: (repos as { nameWithOwner: string }[]).map((r) => r.nameWithOwner), failed: false };
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    // Expected: gh not installed or not authenticated — don't log
    if (msg.includes('command not found') || msg.includes('not logged') || msg.includes('auth login')) {
      return { repos: [], failed: false };
    }
    logWarning('fetchRecentRepos failed', { error: msg });
    return { repos: [], failed: true };
  }
}

/**
 * Search the user's repos + org repos for a given name (case-insensitive).
 * Returns matching "owner/repo" strings.
 */
async function searchReposByName(name: string): Promise<string[]> {
  if (!SAFE_SHELL_ID.test(name)) return [];

  try {
    const userJson = await runOutput('gh api user', 5_000);
    const user = JSON.parse(userJson);
    const username: string = user.login;
    if (!username) return [];

    const orgsPromise = runOutput('gh api user/orgs --jq ".[].login"', 5_000)
      .then((o) => o.trim().split('\n').filter(Boolean))
      .catch((err) => {
        logWarning('searchReposByName: failed to fetch orgs', { error: err instanceof Error ? err.message : String(err) });
        return [] as string[];
      });

    const owners = [username, ...(await orgsPromise)];
    const matches: string[] = [];
    const lowerName = name.toLowerCase();

    await Promise.all(
      owners.map(async (owner) => {
        // Validate owner to prevent shell injection (values come from API responses)
        if (!SAFE_SHELL_ID.test(owner)) {
          logWarning('searchReposByName: skipping owner with unsafe name', { owner });
          return;
        }
        try {
          const json = await runOutput(
            `gh repo list ${owner} --json nameWithOwner --limit 100`,
            5_000,
          );
          const repos = JSON.parse(json) as { nameWithOwner: string }[];
          for (const r of repos) {
            const repoName = r.nameWithOwner.split('/')[1];
            if (repoName?.toLowerCase() === lowerName) {
              matches.push(r.nameWithOwner);
            }
          }
        } catch (err) {
          logWarning('searchReposByName: failed to list repos for owner', {
            owner,
            error: err instanceof Error ? err.message : String(err),
          });
        }
      }),
    );

    return matches;
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    if (msg.includes('command not found') || msg.includes('not logged') || msg.includes('auth login')) {
      return [];
    }
    logWarning('searchReposByName failed', { error: msg });
    console.error(chalk.dim(`  Warning: GitHub repo search failed (${msg})`));
    return [];
  }
}

/**
 * Fetch active sessions from the backend API (wire response field is still
 * named `sandboxes` — see packages/backend/src/handlers/sandboxes.ts, out of
 * scope for this rename pass).
 * Returns `{ sessions, failed }` so callers can distinguish "no sessions" from "fetch error".
 */
async function fetchRemoteSessions(): Promise<{ sessions: SessionRecord[]; failed: boolean }> {
  try {
    const token = await resolveAuthToken();
    const res = await fetch(`${DEFAULT_API_URL}/api/sandboxes`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: AbortSignal.timeout(5_000),
    });
    if (!res.ok) {
      logWarning('fetchRemoteSessions: backend error', { status: res.status });
      return { sessions: [], failed: true };
    }
    const data = await res.json();
    if (!data || !Array.isArray(data.sandboxes)) {
      logWarning('fetchRemoteSessions: unexpected response shape', {});
      return { sessions: [], failed: true };
    }
    const sandboxes = data.sandboxes as {
      id: string;
      name: string | null;
      repo: string | null;
      status: string;
      created_at: string;
      idle_timeout_minutes: number;
    }[];
    return {
      sessions: sandboxes
        .filter((s) => s.status === 'running')
        .map((s) => ({
          id: s.id,
          name: s.name ?? undefined,
          repo: s.repo ?? undefined,
          status: 'running' as const,
          createdAt: s.created_at,
          idleTimeoutMinutes: s.idle_timeout_minutes,
        })),
      failed: false,
    };
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    // Not authenticated is expected (user hasn't logged in yet)
    if (msg.includes('Not authenticated')) {
      return { sessions: [], failed: false };
    }
    logWarning('fetchRemoteSessions failed', { error: msg });
    return { sessions: [], failed: true };
  }
}

/**
 * Merge local and remote sessions, deduplicating by ID.
 * Remote sessions fill in gaps (e.g. launched from another device or the PWA).
 */
function mergeSessions(local: SessionRecord[], remote: SessionRecord[]): SessionRecord[] {
  const seen = new Set(local.map((s) => s.id));
  const merged = [...local];
  for (const r of remote) {
    if (!seen.has(r.id)) {
      merged.push(r);
      seen.add(r.id);
    }
  }
  return merged;
}

interface Choice<T> {
  label: string;
  value: T;
}

/**
 * Display a numbered list and let the user pick. Retries on invalid input.
 */
async function promptChoice<T>(choices: Choice<T>[]): Promise<T> {
  const rl = readline.createInterface({
    input: process.stdin,
    output: process.stdout,
  });

  return new Promise((resolve, reject) => {
    let settled = false;

    const ask = () => {
      rl.question(chalk.bold(`\nChoose [1-${choices.length}]: `), (answer) => {
        const num = parseInt(answer.trim(), 10);
        if (isNaN(num) || num < 1 || num > choices.length) {
          console.error(chalk.red(`Invalid choice "${answer.trim()}". Enter a number between 1 and ${choices.length}.`));
          ask();
        } else {
          settled = true;
          rl.close();
          resolve(choices[num - 1].value);
        }
      });
    };

    rl.on('close', () => {
      if (!settled) reject(new Error('Cancelled.'));
    });

    ask();
  });
}

/**
 * Prompt for free-form text input.
 */
async function promptInput(question: string): Promise<string> {
  const rl = readline.createInterface({
    input: process.stdin,
    output: process.stdout,
  });

  return new Promise((resolve, reject) => {
    let answered = false;
    rl.question(question, (answer) => {
      answered = true;
      rl.close();
      resolve(answer.trim());
    });
    rl.on('close', () => {
      if (!answered) reject(new Error('Cancelled.'));
    });
  });
}

/**
 * Format a relative time string from an ISO date.
 */
function relativeTime(iso: string): string {
  const date = new Date(iso);
  if (isNaN(date.getTime())) return '';
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `${days}d`;
}

/**
 * Extract short repo name from a URL or owner/repo string.
 */
function shortRepo(repo?: string): string {
  if (!repo) return '';
  return repo
    .replace(/^https?:\/\//, '')
    .replace(/^github\.com\//, '')
    .replace(/\.git$/, '');
}

// ── Action types for menu choices ──────────────────────────────────────

interface AgentSessionRecord {
  id: string;
  name: string | null;
  repo_id: string | null;
  status: string;
  message_count?: number;
  last_prompt?: string;
  updated_at: number;
}

/**
 * Fetch recent agent sessions from the sandbox worker.
 */
async function fetchAgentSessions(limit = 5): Promise<{ sessions: AgentSessionRecord[]; failed: boolean }> {
  try {
    const token = await resolveAuthToken();
    const ttyUrl = process.env.SANDBOX_WORKER_URL ?? SANDBOX_WORKER_URL;
    const res = await fetch(`${ttyUrl}/api/agent/sessions?limit=${limit}`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: AbortSignal.timeout(5_000),
    });
    if (!res.ok) return { sessions: [], failed: true };
    const data = (await res.json()) as { sessions: AgentSessionRecord[] };
    return { sessions: data.sessions ?? [], failed: false };
  } catch {
    return { sessions: [], failed: false };
  }
}

type HubAction =
  | { kind: 'attach'; nameOrId: string; destroy?: boolean }
  | { kind: 'resume-agent'; sessionId: string }
  | { kind: 'launch-repo'; repo: string }
  | { kind: 'new-project' }
  | { kind: 'enter-repo' };

// ── Shared rendering helpers ───────────────────────────────────────────

function renderSessionChoices(
  sessions: SessionRecord[],
  choices: Choice<HubAction>[],
  startIdx: number,
  destroy?: boolean,
): number {
  let idx = startIdx;
  console.log(chalk.dim('  Sessions:'));
  for (const s of sessions) {
    idx++;
    const name = s.name ?? s.id;
    const idPart = s.name ? chalk.dim(` (${s.id})`) : '';
    const time = chalk.dim(relativeTime(s.createdAt));
    const repo = chalk.dim(shortRepo(s.repo));
    console.log(`    ${chalk.cyan(String(idx))}  ${name}${idPart}  ${time}  ${repo}`);
    choices.push({
      label: name,
      value: { kind: 'attach', nameOrId: s.name ?? s.id, destroy },
    });
  }
  console.log('');
  return idx;
}

function renderAgentSessionChoices(
  sessions: AgentSessionRecord[],
  choices: Choice<HubAction>[],
  startIdx: number,
): number {
  let idx = startIdx;
  console.log(chalk.dim('  Agent sessions:'));
  for (const s of sessions) {
    idx++;
    const statusColor = s.status === 'active' ? chalk.green : s.status === 'idle' ? chalk.yellow : chalk.gray;
    const label = s.name ? chalk.cyan(s.name) : chalk.gray(s.id.slice(0, 8));
    const repo = s.repo_id ? chalk.dim(` ${s.repo_id}`) : '';
    const msgs = s.message_count ? chalk.dim(` ${s.message_count} msgs`) : '';
    const age = relativeTime(new Date(s.updated_at).toISOString());
    console.log(`    ${chalk.cyan(String(idx))}  ${label}  ${statusColor(s.status)}${repo}${msgs}  ${chalk.dim(age)}`);
    if (s.last_prompt) {
      let preview = '';
      try {
        const parsed = JSON.parse(s.last_prompt);
        preview = typeof parsed === 'string' ? parsed : String(parsed);
      } catch {
        preview = String(s.last_prompt);
      }
      if (preview.length > 50) preview = preview.slice(0, 47) + '...';
      console.log(`       ${chalk.dim(`> ${preview}`)}`);
    }
    choices.push({ label: s.name ?? s.id, value: { kind: 'resume-agent', sessionId: s.id } });
  }
  console.log('');
  return idx;
}

function renderRepoChoices(
  repos: string[],
  choices: Choice<HubAction>[],
  startIdx: number,
  label = 'Recent repos',
): number {
  let idx = startIdx;
  console.log(chalk.dim(`  ${label}:`));
  for (const repo of repos) {
    idx++;
    console.log(`    ${chalk.cyan(String(idx))}  ${repo}`);
    choices.push({
      label: repo,
      value: { kind: 'launch-repo', repo },
    });
  }
  console.log('');
  return idx;
}

// ── Public API ─────────────────────────────────────────────────────────

/**
 * Interactive hub — shown when the user runs `spacecode` with no arguments.
 * Displays active sessions, recent repos, and new-project options.
 */
export async function interactiveHub(options: Record<string, any>): Promise<void> {
  addBreadcrumb('interactive-hub', 'cli', 'info');

  // Load local sessions, remote sessions, agent sessions, and recent repos in parallel
  const [allSessions, remoteResult, agentResult, repoResult] = await Promise.all([
    loadSessions(),
    fetchRemoteSessions(),
    fetchAgentSessions(),
    fetchRecentRepos(),
  ]);

  const active = activeSessions(mergeSessions(allSessions, remoteResult.sessions)).sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  // If nothing to show, fall through to original launch flow
  if (active.length === 0 && agentResult.sessions.length === 0 && repoResult.repos.length === 0) {
    if (repoResult.failed) {
      console.log(chalk.dim('  (Could not fetch recent repos. Is `gh` installed and authenticated?)'));
    }
    if (remoteResult.failed) {
      console.log(chalk.dim('  (Could not fetch remote sessions. Check your connection or run `spacecode login`.)'));
    }
    const { launchWithOptions } = await import('./index.js');
    return launchWithOptions(options);
  }

  console.log(chalk.bold('\nspacecode\n'));

  const choices: Choice<HubAction>[] = [];
  let idx = 0;

  if (agentResult.sessions.length > 0) {
    idx = renderAgentSessionChoices(agentResult.sessions, choices, idx);
  }

  if (active.length > 0) {
    idx = renderSessionChoices(active, choices, idx, options.destroy);
  }

  if (repoResult.repos.length > 0) {
    idx = renderRepoChoices(repoResult.repos, choices, idx);
  }

  // Fixed options
  idx++;
  console.log(`    ${chalk.cyan(String(idx))}  ${chalk.green('New project')}`);
  choices.push({ label: 'New project', value: { kind: 'new-project' } });

  idx++;
  console.log(`    ${chalk.cyan(String(idx))}  ${chalk.green('Enter repo URL')}`);
  choices.push({ label: 'Enter repo URL', value: { kind: 'enter-repo' } });

  try {
    const action = await promptChoice(choices);
    await executeAction(action, options);
  } catch (err) {
    if (err instanceof Error && err.message === 'Cancelled.') {
      process.exit(130);
    }
    const msg = err instanceof Error ? err.message : String(err);
    logWarning('interactive action failed', { error: msg });
    console.error(chalk.red(`Error: ${msg}`));
    process.exit(1);
  }
}

/**
 * Resolve a bare-name target (e.g. `spacecode my-project`).
 * Searches sessions and repos, auto-selects if exactly one match.
 */
export async function resolveTarget(target: string, options: Record<string, any>): Promise<void> {
  addBreadcrumb('resolve-target', 'cli', 'info');

  // Search sessions (local + remote) and repos in parallel
  const [allSessions, remoteResult, matchingRepos] = await Promise.all([
    loadSessions(),
    fetchRemoteSessions(),
    searchReposByName(target),
  ]);

  const active = activeSessions(mergeSessions(allSessions, remoteResult.sessions));
  const lowerTarget = target.toLowerCase();

  // Exact matches first, then substring matches
  const exactSessions = active.filter(
    (s) =>
      s.name?.toLowerCase() === lowerTarget ||
      s.id.toLowerCase() === lowerTarget,
  );
  const fuzzySessions = exactSessions.length > 0
    ? []
    : active.filter((s) => {
        const name = s.name?.toLowerCase();
        const id = s.id.toLowerCase();
        return (
          (name !== undefined && name.includes(lowerTarget) && name !== lowerTarget) ||
          (id.startsWith(lowerTarget) && id !== lowerTarget)
        );
      });
  const matchingSessions = [...exactSessions, ...fuzzySessions];

  const totalMatches = matchingSessions.length + matchingRepos.length;

  // ── Zero matches → error ──────────────────────────────────────────
  if (totalMatches === 0) {
    console.error(chalk.red(`\nNo sessions or repos matching "${target}".`));
    if (remoteResult.failed) {
      console.log(chalk.dim('  (Remote session lookup failed. The session may exist but could not be reached.)'));
    }
    console.log(chalk.dim('Run `spacecode launch --name <name>` to start a new named session.'));
    console.log(chalk.dim('Run `spacecode launch -R owner/repo` to launch with a specific repo.'));
    process.exit(1);
  }

  // ── Exactly one match → auto-select ───────────────────────────────
  if (totalMatches === 1) {
    const isFuzzy = exactSessions.length === 0;
    if (matchingSessions.length === 1) {
      const s = matchingSessions[0];
      const label = s.name ?? s.id;
      const prefix = isFuzzy ? 'Fuzzy match: attaching to' : 'Attaching to session';
      console.log(chalk.dim(`${prefix} "${label}"...`));
      const { attach } = await import('./commands/attach.js');
      return attach(label, { destroy: options.destroy });
    } else {
      const repo = matchingRepos[0];
      console.log(chalk.dim(`Launching with repo ${repo}...`));
      const { launchWithOptions } = await import('./index.js');
      return launchWithOptions({ ...options, repo });
    }
  }

  // ── Multiple matches → display menu ───────────────────────────────
  console.log(chalk.bold(`\nspacecode ${target}\n`));

  const choices: Choice<HubAction>[] = [];
  let idx = 0;

  if (matchingSessions.length > 0) {
    idx = renderSessionChoices(matchingSessions, choices, idx, options.destroy);
  }

  if (matchingRepos.length > 0) {
    idx = renderRepoChoices(matchingRepos, choices, idx, 'Repos');
  }

  try {
    const action = await promptChoice(choices);
    await executeAction(action, options);
  } catch (err) {
    if (err instanceof Error && err.message === 'Cancelled.') {
      process.exit(130);
    }
    const msg = err instanceof Error ? err.message : String(err);
    logWarning('interactive action failed', { error: msg });
    console.error(chalk.red(`Error: ${msg}`));
    process.exit(1);
  }
}

// ── Execute a chosen action ────────────────────────────────────────────

async function executeAction(action: HubAction, _options: Record<string, any>): Promise<void> {
  switch (action.kind) {
    case 'attach': {
      const { startInteractiveSession } = await import('./agent/interactive.js');
      return startInteractiveSession({ sessionId: action.nameOrId });
    }
    case 'resume-agent': {
      const { startInteractiveSession } = await import('./agent/interactive.js');
      return startInteractiveSession({ sessionId: action.sessionId });
    }
    case 'launch-repo': {
      const { startInteractiveSession } = await import('./agent/interactive.js');
      return startInteractiveSession({ repo: action.repo });
    }
    case 'new-project': {
      const { startInteractiveSession } = await import('./agent/interactive.js');
      return startInteractiveSession({});
    }
    case 'enter-repo': {
      const url = await promptInput(chalk.bold('  Repo URL or owner/repo: '));
      if (!url) {
        console.error(chalk.red('No repo provided.'));
        process.exit(1);
      }
      const { startInteractiveSession } = await import('./agent/interactive.js');
      return startInteractiveSession({ repo: url });
    }
    default: {
      const _exhaustive: never = action;
      throw new Error(`Unhandled action kind: ${(_exhaustive as HubAction).kind}`);
    }
  }
}
